import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { setTag, getCurrentScope, setContext, captureMessage } from '@sentry/gatsby';
import { Alert, Button, Modal, Select, TextArea, TextInput } from '@pella/sash-framework';
import { LoadingOverlay } from '../../../..';
import LoginForm from '../../../account/common/login-form';
import ItemGrouping from '../../common/item-grouping';
import styles from './add-to-project.module.scss';
import { useProject } from '../../../../../hooks/use-project';
import { useCart } from '../../../../../hooks/use-cart';

const AddToProject = (props) => {
	const [modalOpen, setModalOpen] = useState('');
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const { addItemToProject, createProjectWithItem, getProjects } = useProject();
	const { copyItemFromCart } = useCart();
	const [projects, setProjects] = useState(null);

	function closeModal() {
		setModalOpen('');
		setError('');
		setProjectDetails({
			selectedProject: '',
			projectName: '',
			projectDescription: '',
		});
		if (props.isFromCart && modalOpen === 'projectConfirmation') {
			props.reloadCart();
		}
	}

	function onAuthChangeModal() {
		setModalOpen('projects');
	}

	const [projectDetails, setProjectDetails] = useState({
		selectedProject: '',
		projectName: '',
		projectDescription: '',
	});
	const { selectedProject, projectName, projectDescription } = projectDetails;

	const handleChange = (e) => {
		setProjectDetails({
			...projectDetails,
			[e.target.name]: e.target.value,
		});
	};

	const saveToNewProject = () => {
		setError('');
		if (projectName) {
			setLoading(true);
			saveItemToNewProject(projectName, projectDescription);
		}
	};

	const saveItemToNewProject = async (name, description = '') => {
		try {
			let project;
			if (props.isFromCart) {
				const targetObj = {
					target_type: 'project',
					name: name,
					description: description,
				};

				project = await copyItemFromCart(props.currentItem.sku, targetObj, props.clientID);
			} else {
				project = await createProjectWithItem(props.clientID, name, props.configurationURL, props.configKey, description);
			}

			if (project.data.Project.id) {
				setProjectDetails({
					selectedProject: project.data.Project.id,
				});
				setModalOpen('projectConfirmation');
				setLoading(false);
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: 'createProject',
				});
				window.dataLayer.push({
					event: 'addToProject',
					project: {
						add: {
							id: project.Project.Group.Items[0].brand_name + ' ' + project.Project.Group.Items[0].product_name,
							name: project.Project.Group.Items[0].brand_name,
							category: project.Project.Group.Items[0].product_name,
						},
					},
				});
			} else if (project.data.error) {
				setTag('Pella Component', 'AddToProject', 'API Transaction ID', project?.data?.REQUEST_ID);
				getCurrentScope().setTransactionName('createProjectWithItem');
				setContext('Error', {
					errorData: JSON.stringify(project?.data) || 'Unknown Error Data',
					responseURL: project?.request?.responseURL,
					configData: project?.config?.data,
				});
				captureMessage(project?.data?.error?.message || 'Unknown Error Details');

				setError(project.data.error);
				setLoading(false);
			}
		} catch (error) {
			setError(error);
			setLoading(false);
		}
	};

	const saveToExistingProject = () => {
		setError('');
		let groupID = '';
		for (let i = 0; i < projects.length; i++) {
			if (selectedProject === projects[i].id) {
				groupID = projects[i].default_group;
				break;
			}
		}
		if (selectedProject && groupID) {
			setLoading(true);
			addToProject(selectedProject, groupID);
		}
	};

	const addToProject = async (projectID, groupID) => {
		try {
			let project;
			if (props.isFromCart) {
				const targetObj = {
					target_type: 'project',
					target_quote_id: projectID,
					group_id: groupID,
				};
				project = await copyItemFromCart(props.currentItem.sku, targetObj, props.clientID);
			} else {
				project = await addItemToProject(props.clientID, projectID, groupID, props.configKey, props.configurationURL);
			}
			// eslint-disable-next-line react/prop-types -- fix this
			if (project.data.id) {
				setModalOpen('projectConfirmation');
				setLoading(false);
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: 'addToProject',
					project: {
						add: {
							id: project.data.brand_name + ' ' + project.data.product_name,
							name: project.data.brand_name,
							category: project.data.product_name,
						},
					},
				});
			} else if (project.data.error) {
				setTag('Pella Component', 'AddToProject', 'API Transaction ID', project?.data?.REQUEST_ID);
				getCurrentScope().setTransactionName('addItemToProject');
				setContext('Error', {
					errorData: JSON.stringify(project?.data) || 'Unknown Error Data',
					responseURL: project?.request?.responseURL,
					configData: project?.config?.data,
				});
				captureMessage(project?.data?.error?.message || 'Unknown Error Details');

				setError(project.data.error);
				setLoading(false);
			}
		} catch (error) {
			setError(error);
			setLoading(false);
		}
	};

	const fetchProjects = useCallback(async () => {
		try {
			setLoading(true);
			setModalOpen('projects');
			const loadProjects = await getProjects();
			if (loadProjects.data.Projects) {
				setProjects(loadProjects.data.Projects);
				setLoading(false);
			} else if (loadProjects.data.error) {
				setTag('Pella Component', 'FetchProjects', 'API Transaction ID', loadProjects?.data?.REQUEST_ID);
				getCurrentScope().setTransactionName('fetchProjects');
				setContext('Error', {
					errorData: JSON.stringify(loadProjects?.data) || 'Unknown Error Data',
					responseURL: loadProjects?.request?.responseURL,
					configData: loadProjects?.config?.data,
				});
				captureMessage(loadProjects?.data?.error?.message || 'Unknown Error Details');
				setError(loadProjects.data.error);
				setLoading(false);
			}
		} catch (error) {
			setError(error);
			setLoading(false);
		}
	}, [getProjects]);

	useEffect(() => {
		if (modalOpen === 'projects' && props.userSignedIn && projects == null) {
			fetchProjects();
		}
	}, [fetchProjects, modalOpen, projects, props.userSignedIn]);

	return (
		<>
			<div className={`${props.className ? props.className : ''}`}>
				<Button
					color='secondary'
					onClick={props.userSignedIn ? () => fetchProjects() : () => setModalOpen('login')}
					disabled={props.limitations !== null && 0 in props.limitations}
					className={`${styles.projectsButton}`}
				>
					save to project
				</Button>
				{props.message && <p className={styles.projectsText}>{props.message}</p>}
			</div>

			{/* Sign In Modal */}
			{modalOpen === 'login' && (
				<Modal
					effect='slidedown'
					isOpen={modalOpen === 'login'}
					onClose={closeModal}
					width={1000}
				>
					<Modal.Header headingLevel={1}>&nbsp;</Modal.Header>
					<Modal.Content>
						<LoginForm
							type='isModal'
							onAuthCloseModal={onAuthChangeModal}
						/>
					</Modal.Content>
				</Modal>
			)}

			{/* Project Modal */}
			{modalOpen === 'projects' && (
				<Modal
					effect='slidedown'
					isOpen={modalOpen === 'projects'}
					onClose={closeModal}
					width={800}
				>
					{(!Array.isArray(projects) || loading || props.loading) && <LoadingOverlay fixed={false} />}
					<Modal.Content>
						{projects?.length > 0 && (
							<div className={styles.existingProject}>
								<h2 className='mb-4 text-xl font-semibold md:mb-6 md:text-2xl'>Select an Existing Project</h2>
								<Select
									id='selectedProject'
									labelText='Project Name'
									value={selectedProject}
									required={true}
									onChange={handleChange}
								>
									<option value=''>Please Select...</option>
									{projects.map((project) => (
										<option
											key={project.id}
											value={project.id}
										>
											{project.name.length > 50 ? project.name.substring(0, 50) + '\u2026' : project.name}
										</option>
									))}
								</Select>
								<Button
									size='sm'
									onClick={saveToExistingProject}
								>
									Select Project
								</Button>
							</div>
						)}

						<h2 className='mb-4 mt-6 text-xl font-semibold md:mb-6 md:mt-8 md:text-2xl'>{projects?.length > 0 ? '...or Create a New Project' : 'Create a New Project'}</h2>
						<TextInput
							id='projectName'
							labelText='Project Name'
							maxLength={30}
							value={projectName}
							required={true}
							onChange={handleChange}
						/>
						<p className={`mt-1 text-right text-sm ${projectDetails.projectName.length === 30 ? 'text-red' : ''}`}>
							{projectDetails.projectName.length}/{30}
						</p>
						<TextArea
							id='projectDescription'
							labelText='Project Description'
							value={projectDescription}
							onChange={handleChange}
						/>
						{error !== '' && projects?.Projects?.length > 0 && (
							<Alert
								type='error'
								className='mb-4'
								hideClose
							>
								{typeof error?.message === 'string' && <p>Error Message: {error?.message}</p>}
								{typeof error?.details === 'string' && <p>Details: {error.details}</p>}
							</Alert>
						)}
						<Button
							size='sm'
							onClick={saveToNewProject}
						>
							Create Project
						</Button>
					</Modal.Content>
				</Modal>
			)}

			{/* Project Confirmation Modal */}
			{modalOpen === 'projectConfirmation' && (
				<Modal
					effect='slidedown'
					isOpen={modalOpen === 'projectConfirmation'}
					onClose={closeModal}
					width={1000}
				>
					<Modal.Header headingLevel={1}>&nbsp;</Modal.Header>

					<Modal.Content className={styles.pdpModal_quoteContent}>
						<h1 className='mb-4 text-xl font-semibold md:mb-6 md:text-2xl'>
							{props.currentItem.quantity} Item
							{props.currentItem.quantity > 1 ? 's' : ''} Added!
						</h1>
						<ItemGrouping
							modal={'projects'}
							itemImage={props.currentItem.drawings?.current_drawing?.url || props.currentItem.product_image_url}
							itemImageName={props.currentItem.drawings?.current_drawing?.display_name}
							// itemProductLine={props.itemContent.product_line}
							// itemName={props.itemContent.product}
							quantity={props.currentItem.quantity}
							hasCart={false}
							projectID={selectedProject ? selectedProject : projects?.Project?.id}
							qanda={props?.currentItem?.current_step_data?.questions}
							loading={loading}
							detailsHeading={props.detailsHeading}
							// eslint-disable-next-line react/prop-types -- fix this
							fullName={props.fullName}
							cartReturn={props.isFromCart}
						/>
					</Modal.Content>
				</Modal>
			)}
		</>
	);
};

AddToProject.propTypes = {
	currentItem: PropTypes.object,
	limitations: PropTypes.array,
	message: PropTypes.string,
	userSignedIn: PropTypes.bool,
	clientID: PropTypes.string,
	configKey: PropTypes.string,
	itemContent: PropTypes.object,
	detailsHeading: PropTypes.string,
	loading: PropTypes.bool,
	className: PropTypes.string,
	isFromCart: PropTypes.bool,
	configurationURL: PropTypes.string,
	reloadCart: PropTypes.func,
	redirectUrl: PropTypes.string,
};

export default AddToProject;
